import Phaser from 'phaser';
import { audio } from '../audio/instances';
import { t } from '../i18n';
import { PALETTE, PANEL_BG } from '../theme';
import { SettingsDialog } from '../ui/SettingsDialog';

export class MainMenuScene extends Phaser.Scene {
  private dom: HTMLDivElement | null = null;
  private exitOverlay: HTMLDivElement | null = null;
  private tutorialWarnOverlay: HTMLDivElement | null = null;
  private settingsDialog = new SettingsDialog();
  private onLocaleChanged = () => this.rebuildDom();

  constructor() {
    super({ key: 'MainMenu' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);
    // Preload music playlist + common SFX in background so they're instant
    audio.music.preload();
    audio.fileSfx.preload('select-barracks', 'select-factory', 'select-shipyard', 'turn-start');

    // Start shuffled music playlist. AudioContext may be suspended (browser
    // gesture policy) — the source is scheduled eagerly and begins playing
    // as soon as the global gesture handler in instances.ts resumes it.
    if (!audio.settings.muted) {
      audio.music.play();
    }
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);

    window.addEventListener('locale-changed', this.onLocaleChanged);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private rebuildDom(): void {
    this.dom?.remove();
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
  }

  private buildDom(): HTMLDivElement {
    const dom = document.createElement('div');
    // Shared button style — image background with military-style label
    const btnBase = `
      display:block;width:245px;height:46px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;
      font-weight:bold;font-size:14px;letter-spacing:3px;text-transform:uppercase;
      padding:0 0 0 56px;text-align:center;line-height:46px;
      transition:filter 0.15s;
    `.replace(/\n\s*/g, '');
    const hoverCss = `
      .menu-btn:hover { filter: brightness(1.2); }
      .menu-btn:active { filter: brightness(0.9); transform: scale(0.97); }
      .menu-btn { transition: filter 0.15s, transform 0.08s; }
    `;

    Object.assign(dom.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '14px',
      color: '#d8dee9',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      background: '#0b0d10 url("wallpaper.png") center/cover no-repeat',
      zIndex: '10',
    } as Partial<CSSStyleDeclaration>);
    dom.innerHTML = `
      <style>${hoverCss}</style>
      <div style="font-size:54px;font-weight:700;letter-spacing:4px;color:#E8DCC2;font-family:'Trebuchet MS','Arial Black',Impact,sans-serif;text-shadow:2px 3px 6px rgba(0,0,0,0.7)">GIERKA</div>
      <div style="display:flex;flex-direction:column;gap:4px;margin-top:32px;align-items:center;transform:translateX(-28px)">
        <button id="btn-newgame" class="menu-btn" style="${btnBase}background-image:url('/graphics/ui/new-game-button-background.png')">${t('menu.newGame')}</button>
        <button id="btn-tutorial" class="menu-btn" disabled title="${t('campaign.locked')}" style="${btnBase}background-image:url('/graphics/ui/tutorial-button-background.png');opacity:0.4;filter:grayscale(1);cursor:not-allowed;pointer-events:none">${t('menu.tutorial')}</button>
        <button id="btn-load" class="menu-btn" disabled title="${t('campaign.locked')}" style="${btnBase}background-image:url('/graphics/ui/load-game-button-background.png');opacity:0.4;filter:grayscale(1);cursor:not-allowed;pointer-events:none">${t('menu.loadGame')}</button>
        <button id="btn-settings" class="menu-btn" style="${btnBase}background-image:url('/graphics/ui/settings-button-background.png')">${t('menu.settings')}</button>
        <button id="btn-exit" class="menu-btn" style="${btnBase}background-image:url('/graphics/ui/exit-button-background.png')">${t('menu.exitGame')}</button>
      </div>
      <div style="opacity:0.4;font-size:11px;position:absolute;bottom:16px">${t('menu.hint')}</div>
    `;
    dom.querySelector<HTMLButtonElement>('#btn-newgame')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.scene.start('GameModeSelect');
    });
    dom.querySelector<HTMLButtonElement>('#btn-tutorial')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.showTutorialExperimentalWarning();
    });
    dom.querySelector<HTMLButtonElement>('#btn-load')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      // TODO: load game implementation
    });
    dom.querySelector<HTMLButtonElement>('#btn-settings')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.settingsDialog.open(undefined, true);
    });
    dom.querySelector<HTMLButtonElement>('#btn-exit')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.showExitConfirm();
    });
    return dom;
  }

  private showExitConfirm(): void {
    if (this.exitOverlay) return;

    const smallBtnCss = `
      display:inline-block;height:46px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;
      font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;
      line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s;
    `.replace(/\n\s*/g, '');

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(8,10,14,0.78)', zIndex: '20',
    } as Partial<CSSStyleDeclaration>);

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '8px',
      padding: '32px 44px',
      display: 'flex', flexDirection: 'column', gap: '18px', alignItems: 'center',
    } as Partial<CSSStyleDeclaration>);

    card.innerHTML = `
      <style>
        .exit-btn:hover { filter: brightness(1.2); }
        .exit-btn:active { filter: brightness(0.9); transform: scale(0.97); }
      </style>
      <div style="font-size:18px;font-weight:bold;color:#E8DCC2;text-align:center;
        font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;letter-spacing:2px">${t('menu.exitConfirm')}</div>
      <div style="display:flex;gap:12px;justify-content:center">
        <button id="exit-yes" class="exit-btn" style="${smallBtnCss}width:150px;background-image:url('/graphics/ui/confirmation-button-background.png')">${t('menu.yes')}</button>
        <button id="exit-no" class="exit-btn" style="${smallBtnCss}width:150px;background-image:url('/graphics/ui/cancel-button-background.png')">${t('menu.no')}</button>
      </div>
    `;

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.exitOverlay = overlay;

    // Dialog closes only via buttons, not by clicking outside.
    card.querySelector<HTMLButtonElement>('#exit-yes')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      window.close();
    });
    card.querySelector<HTMLButtonElement>('#exit-no')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.closeExitConfirm();
    });
  }

  private closeExitConfirm(): void {
    this.exitOverlay?.remove();
    this.exitOverlay = null;
  }

  private showTutorialExperimentalWarning(): void {
    if (this.tutorialWarnOverlay) return;

    const ackBtnCss = `
      display:inline-block;height:46px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;
      font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;
      line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s;
    `.replace(/\n\s*/g, '');

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(8,10,14,0.78)', zIndex: '20',
    } as Partial<CSSStyleDeclaration>);

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '8px',
      padding: '32px 44px', maxWidth: '520px',
      display: 'flex', flexDirection: 'column', gap: '18px', alignItems: 'center',
    } as Partial<CSSStyleDeclaration>);

    card.innerHTML = `
      <style>
        .exit-btn:hover { filter: brightness(1.2); }
        .exit-btn:active { filter: brightness(0.9); transform: scale(0.97); }
      </style>
      <div style="font-size:18px;font-weight:bold;color:#E8DCC2;text-align:center;
        font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;letter-spacing:2px">${t('menu.tutorialExperimentalTitle')}</div>
      <div style="font-size:14px;color:#d8dee9;text-align:center;line-height:1.45;
        font-family:system-ui,-apple-system,'Segoe UI',sans-serif">${t('menu.tutorialExperimentalBody')}</div>
      <button id="tutorial-warn-ack" class="exit-btn" style="${ackBtnCss}width:200px;background-image:url('/graphics/ui/confirmation-button-background.png')">${t('menu.tutorialExperimentalAck')}</button>
    `;

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.tutorialWarnOverlay = overlay;

    card.querySelector<HTMLButtonElement>('#tutorial-warn-ack')!.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.closeTutorialExperimentalWarning();
      this.scene.start('Tutorial');
    });
  }

  private closeTutorialExperimentalWarning(): void {
    this.tutorialWarnOverlay?.remove();
    this.tutorialWarnOverlay = null;
  }

  private teardown(): void {
    window.removeEventListener('locale-changed', this.onLocaleChanged);
    this.settingsDialog.close();
    this.closeExitConfirm();
    this.closeTutorialExperimentalWarning();
    this.dom?.remove();
    this.dom = null;
  }
}
